import { Component, OnInit } from '@angular/core';
import { formatUsd } from '../../shared/formatting/usd-currency';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderToolbarComponent } from '../../shared/header-toolbar/header-toolbar.component';
import { QuoteService } from '../../services/quote.service';
import { AuthService } from '../../services/auth.service';
import { QuoteUtilsService } from '../../services/quote-utils.service';
import { Quote, QuoteStatus, Payment, PaymentType, Coverage } from '../../models/quote.model';
import { UserInfo } from '../../models/auth.model';
import { EmailQuoteModalComponent, EmailQuoteModalData } from '../../shared/email-quote-modal/email-quote-modal.component';
import { PdfPreviewDialogComponent, PdfPreviewDialogData } from '../../shared/pdf-preview-dialog/pdf-preview-dialog.component';

@Component({
  selector: 'app-view-quote',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    HeaderToolbarComponent
  ],
  templateUrl: './view-quote.component.html',
  styleUrls: ['./view-quote.component.scss']
})
export class ViewQuoteComponent implements OnInit {
  quote: Quote | null = null;
  loading = true;
  pdfDownloading = false;
  currentUser: UserInfo | null = null;

  paymentsColumns: string[] = ['paymentDate', 'paymentType', 'stripePaymentId', 'status', 'amount', 'netAmount'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private quoteService: QuoteService,
    private authService: AuthService,
    private quoteUtils: QuoteUtilsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (!this.currentUser) {
      this.router.navigate(['/login']);
      return;
    }

    const quoteId = this.route.snapshot.params['id'];
    if (quoteId) {
      this.loadQuote(+quoteId);
    } else {
      this.router.navigate(['/quotes']);
    }
  }

  loadQuote(id: number): void {
    this.loading = true;
    this.quoteService.getQuote(id).subscribe({
      next: (quote) => {
        this.quote = quote;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading quote:', error);
        this.snackBar.open('Failed to load quote. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
        this.loading = false;
        this.router.navigate(['/quotes']);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/quotes']);
  }

  editQuote(): void {
    if (this.quote) {
      this.router.navigate(['/quote', this.quote.id]);
    }
  }

  downloadPdf(): void {
    if (!this.quote) return;

    this.pdfDownloading = true;
    this.quoteService.downloadQuotePdf(this.quote.id).subscribe({
      next: (blob) => {
        this.pdfDownloading = false;
        // Preview the agreement in a popup first; the dialog carries its own Download button.
        this.dialog.open(PdfPreviewDialogComponent, {
          data: {
            blob,
            fileName: 'premium-finance-agreement.pdf',
            title: 'Premium Finance Agreement'
          } as PdfPreviewDialogData,
          panelClass: 'pdf-preview-panel',
          maxWidth: '95vw',
          autoFocus: false
        });
      },
      error: () => {
        this.pdfDownloading = false;
        this.snackBar.open('Could not download the PDF. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });
  }

  emailQuote(): void {
    if (!this.quote) return;

    const dialogData: EmailQuoteModalData = {
      quoteId: this.quote.id,
      customerName: this.quote.insuredName,
      customerEmail: this.quote.email
    };

    const dialogRef = this.dialog.open(EmailQuoteModalComponent, {
      width: '500px',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Optionally show a success message
        console.log('Email sent successfully');
      }
    });
  }

  // Delegate methods to quoteUtils service
  getStatusChipColor(status: QuoteStatus): string {
    return this.quoteUtils.getStatusChipColor(status);
  }

  getStatusDisplayName(status: QuoteStatus): string {
    return this.quoteUtils.getStatusDisplayName(status);
  }

  formatCurrency(amount: number): string {
    return this.quoteUtils.formatCurrency(amount);
  }

  formatDate(date: Date | string): string {
    return this.quoteUtils.formatDate(date);
  }

  /** Human-readable coverage label (enum → text) for the Policies section. */
  getCoverageDisplayName(coverage: Coverage | number | undefined): string {
    switch (coverage) {
      case Coverage.Aircraft: return 'Aircraft';
      default:                return 'Aircraft';
    }
  }

  getPaymentTypeDisplayName(type: PaymentType | undefined): string {
    if (type === undefined || type === null) {
      return '—';
    }
    switch (type) {
      case PaymentType.DownPayment:
        return 'Down payment';
      case PaymentType.MonthlyPayment:
        return 'Monthly payment';
      case PaymentType.LateFee:
        return 'Late fee';
      case PaymentType.Payoff:
        return 'Payoff';
      case PaymentType.Unknown:
      default:
        return 'Unknown';
    }
  }

  getTotalPremium(): number {
    return this.quoteUtils.getTotalPremium(this.quote);
  }

  getTotalPaid(): number {
    return this.quoteUtils.getTotalPaid(this.quote);
  }

  // ===========================================================================
  //  Presentation-only helpers for the black+gold redesign. No data flow or
  //  backend behaviour — figures are derived from the existing Quote model.
  // ===========================================================================

  /** Currency with cents for accurate payment display. */
  formatWhole(amount: number): string {
    return formatUsd(amount);
  }

  /** Sum of the per-policy down payments. */
  getTotalDownPayment(): number {
    if (!this.quote?.policies?.length) {
      return 0;
    }
    return this.quote.policies.reduce((sum, p) => sum + (p.downAmount || 0), 0);
  }

  /** The financed principal (headline "amount financed"). */
  getAmountFinanced(): number {
    return this.quote?.totalFinancingAmount || 0;
  }

  /** Remaining balance = amount financed − total paid. */
  getRemainingBalance(): number {
    return this.getAmountFinanced() - this.getTotalPaid();
  }

  /** Whether this quote carries a financed balance (drives the flight path). */
  isFinanced(): boolean {
    return this.getAmountFinanced() > 0;
  }

  /** Latest recorded monthly installment amount, if any (0 when none). */
  getMonthlyPaymentAmount(): number {
    const monthly = (this.quote?.payments ?? []).filter(p => p.paymentType === PaymentType.MonthlyPayment);
    return monthly.length ? monthly[monthly.length - 1].amount : 0;
  }

  /** Token-driven badge class for a quote status (see .badge--* in the scss). */
  getStatusBadgeClass(status: QuoteStatus): string {
    switch (status) {
      case QuoteStatus.Quoted:     return 'badge--quoted';
      case QuoteStatus.InProgress: return 'badge--inprogress';
      case QuoteStatus.Complete:   return 'badge--complete';
      case QuoteStatus.Grace:      return 'badge--grace';
      case QuoteStatus.Delinquent: return 'badge--delinquent';
      case QuoteStatus.Cancelled:  return 'badge--cancelled';
      default:                     return 'badge--quoted';
    }
  }

  /** Token-driven badge class for a Stripe payment status string. */
  getPaymentStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'succeeded' || s === 'paid') {
      return 'badge--paid';
    }
    if (s === 'failed' || s === 'canceled' || s === 'cancelled') {
      return 'badge--failed';
    }
    return 'badge--pending';
  }

  /** Markers for the signature flight-path timeline (down payment → paid in full). */
  getFlightPathMarkers(): { left: number; bottom: number; type: 'takeoff' | 'mid' | 'end'; label: string }[] {
    const paymentCount = this.quote?.payments?.length ?? 0;
    const nodes = Math.max(paymentCount, 1) + 1; // recorded payments + paid-in-full endpoint
    const out: { left: number; bottom: number; type: 'takeoff' | 'mid' | 'end'; label: string }[] = [];
    for (let i = 0; i < nodes; i++) {
      const t = nodes > 1 ? i / (nodes - 1) : 0;
      const type: 'takeoff' | 'mid' | 'end' = i === 0 ? 'takeoff' : (i === nodes - 1 ? 'end' : 'mid');
      out.push({
        left: 5 + t * 90,        // 5% → 95% (matches the SVG line x 30 → 570)
        bottom: 20.3 + t * 57,   // 20.3% → 77.3% (matches the SVG line y 102 → 29)
        type,
        label: i === 0 ? 'Takeoff' : (i === nodes - 1 ? 'Paid' : '')
      });
    }
    return out;
  }

  getUserDisplayName(): string {
    return this.authService.getUserDisplayName();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}

